import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Copy, Check, Search, FileDown, Truck, Lock, ChevronDown, ChevronUp, Ban, RefreshCw, XCircle, AlertTriangle } from "lucide-react";
import TopBar from "@/components/TopBar";
import StatusBadge from "@/components/StatusBadge";
import EventStatusBadge from "@/components/EventStatusBadge";
import LastMinuteGiftsAdmin from "@/components/LastMinuteGiftsAdmin";
import EditReservationModal from "@/components/EditReservationModal";
import GiftStatusPill from "@/components/GiftStatusPill";
import CancelGiftModal from "@/components/CancelGiftModal";
import ProductSwapPicker from "@/components/ProductSwapPicker";
import { PromptModal, ConfirmModal } from "@/components/AppDialogs";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { categoryLabel, CATEGORIES } from "@/lib/categories";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function AdminEventDetail() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/events/${id}`);
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [id]);

  const copyCode = () => {
    if (data?.event?.code) {
      navigator.clipboard.writeText(data.event.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const removeFromWishlist = async (productId) => {
    const next = (data.event.wishlist_product_ids || []).filter((x) => x !== productId);
    await api.patch(`/admin/events/${id}`, { wishlist_product_ids: next });
    load();
  };

  const [cancelEventOpen, setCancelEventOpen] = useState(false);
  const [extendPromptFor, setExtendPromptFor] = useState(null); // reservation object
  const [swapReasonFor, setSwapReasonFor] = useState(null); // { newProduct, contribution }

  const cancelEvent = async () => {
    try {
      await api.delete(`/admin/events/${id}`);
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Annulation refusée.");
    }
  };

  const submitExtendReservation = async (raw) => {
    const r = extendPromptFor;
    setExtendPromptFor(null);
    if (!r) return;
    const days = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(days) || days < 1 || days > 60) {
      toast.error("Nombre de jours invalide (entre 1 et 60).");
      return;
    }
    try {
      await api.patch(`/admin/reservations/${r.id}/extend`, { additional_days: days });
      load();
      toast.success(`Cagnotte ${r.code} étendue de ${days} jour(s).`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Extension refusée.");
    }
  };

  const [expandedReservation, setExpandedReservation] = useState(null);
  const [editReservation, setEditReservation] = useState(null);
  const [cancelContribution, setCancelContribution] = useState(null);
  const [swapContribution, setSwapContribution] = useState(null);
  const [expandedOfferedGift, setExpandedOfferedGift] = useState(null);
  const toggleReservation = (id) =>
    setExpandedReservation((cur) => (cur === id ? null : id));

  const downloadOpsPdf = async (type) => {
    try {
      const resp = await api.get(`/admin/events/${id}/ops-pdf?type=${type}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        type === "checklist"
          ? `milkado-checklist-${data?.event?.code || "event"}.pdf`
          : `milkado-livraison-${data?.event?.code || "event"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Téléchargement PDF impossible.");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF9FB]">
        <TopBar />
        <div className="text-center mt-20 text-[#D81B8C]">{error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5C4664]">
        {t("common.loading")}
      </div>
    );
  }

  const { event, wishlist, contributions, reservations, offered_gifts: offeredGifts = [] } = data;
  const isCancelled = event?.status === "cancelled";

  return (
    <div className="min-h-screen bg-[#FFF9FB]">
      <TopBar />
      <div className="max-w-5xl mx-auto px-5 pb-24">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          data-testid="admin-event-back"
          className="gap-2 -ml-3 mt-2 text-[#5C4664] hover:bg-[#FCE7F3]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("admin.backToDashboard")}
        </Button>

        {isCancelled && (
          <div
            data-testid="event-cancelled-banner"
            className="mt-4 flex items-center gap-3 bg-red-50 border-2 border-red-200 rounded-2xl px-4 py-3 text-sm text-red-800"
          >
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
            <div>
              <p className="font-bold">{t("admin.cancelledBanner")}</p>
              {event.cancelled_at && (
                <p className="text-xs text-red-700 mt-0.5">
                  Annulé le {new Date(event.cancelled_at).toLocaleString()}
                  {event.cancelled_by_user ? ` par ${event.cancelled_by_user}` : ""}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <EventStatusBadge status={event.status || "upcoming"} />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C] leading-tight">
              {event.title}
            </h1>
            <p className="mt-2 text-[#5C4664]">{event.host_names} · {event.venue}</p>
            <div className="mt-4 flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => navigate(`/admin/events/${id}/edit`)}
                data-testid="admin-event-edit"
                disabled={isCancelled}
                title={isCancelled ? "Événement annulé — modifications interdites." : undefined}
                className="rounded-full gap-2 border-[#F2E2EC] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Pencil className="w-4 h-4" /> {t("common.edit")}
              </Button>
              {isAdmin && !isCancelled && (
                <Button
                  variant="outline"
                  onClick={() => setCancelEventOpen(true)}
                  data-testid="admin-event-cancel"
                  className="rounded-full gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-400"
                  title="Annuler définitivement cet événement (admin uniquement)."
                >
                  <XCircle className="w-4 h-4" /> {t("admin.cancelEvent")}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => downloadOpsPdf("checklist")}
                data-testid="download-checklist-pdf"
                className="rounded-full gap-2 border-[#F2E2EC] hover:border-[#3FA535] hover:text-[#3FA535]"
              >
                <FileDown className="w-4 h-4" /> {t("admin.opsDownloadChecklist")}
              </Button>
              <Button
                variant="outline"
                onClick={() => downloadOpsPdf("delivery")}
                data-testid="download-delivery-pdf"
                className="rounded-full gap-2 border-[#F2E2EC] hover:border-[#6B2C8F] hover:text-[#6B2C8F]"
              >
                <Truck className="w-4 h-4" /> {t("admin.opsDownloadDelivery")}
              </Button>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-[#2A0E3C] text-white">
            <p className="text-xs font-bold uppercase tracking-widest text-white/70 mb-2">
              {t("admin.shareCode")}
            </p>
            <div className="flex items-center justify-between">
              <span data-testid="event-code-display" className="font-display text-4xl font-black tracking-[0.3em] text-[#3FA535]">
                {event.code}
              </span>
              <button
                onClick={copyCode}
                data-testid="copy-code-btn"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Wishlist */}
        <div className="mt-12">
          <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
            <h2 className="font-display text-2xl font-black text-[#2A0E3C]">
              {t("admin.wishlist")}
            </h2>
            <Button
              onClick={() => setPickerOpen(true)}
              data-testid="add-from-catalog-btn"
              disabled={event.admin_locked}
              className="rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] gap-2 disabled:opacity-60"
            >
              {event.admin_locked ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {t("admin.pickFromCatalog")}
            </Button>
          </div>
          {event.admin_locked && (
            <div
              data-testid="admin-locked-notice"
              className="mb-4 p-3 rounded-xl bg-[#FCE7F3] text-[#A60F6E] text-xs font-medium flex items-start gap-2"
            >
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {t("admin.adminLockedNotice")}
            </div>
          )}

          {wishlist.length === 0 ? (
            <div data-testid="admin-wishlist-empty" className="py-10 text-center text-[#5C4664] bg-white rounded-2xl border border-[#F2E2EC]">
              {t("wishlistPage.noItems")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {wishlist.map((p) => (
                <div
                  key={p.id}
                  data-testid={`admin-wishlist-item-${p.id}`}
                  className="bg-white rounded-2xl p-4 border border-[#F2E2EC]"
                >
                  <div className="flex items-center gap-3">
                    {p.image_url && (
                      <img src={p.image_url} alt="" className="w-14 h-14 rounded-xl object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-base font-black text-[#2A0E3C] leading-tight line-clamp-2">
                        {p.name}
                      </h3>
                      <div className="text-xs text-[#5C4664]">{categoryLabel(p.category, lang)}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <StatusBadge status={p.status} />
                    <span className="font-display text-lg font-black text-[#D81B8C]">
                      ${p.price.toFixed(0)}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-[#5C4664]">
                    ${(p.total_collected || 0).toFixed(0)} {t("admin.collected")} · {p.contributors_count} contrib.
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeFromWishlist(p.id)}
                    data-testid={`remove-wishlist-${p.id}`}
                    className="mt-3 w-full rounded-full border-[#F2E2EC] text-[#D81B8C] hover:bg-[#FCE7F3] hover:text-[#D81B8C]"
                  >
                    <Trash2 className="w-3 h-3 mr-2" /> {t("common.delete")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Last-minute gifts (PIN-protected) */}
        <LastMinuteGiftsAdmin event={event} onChange={load} />

        {/* Reservations */}
        <div className="mt-14">
          <h2 className="font-display text-2xl font-black text-[#2A0E3C] mb-5">
            {t("admin.reservations")} ({reservations.length})
          </h2>
          {reservations.length === 0 ? (
            <div className="py-8 text-center text-[#5C4664] bg-white rounded-2xl border border-[#F2E2EC]">
              {t("admin.noReservations")}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-[#F2E2EC] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FFF9FB] text-[#5C4664] uppercase text-xs tracking-widest">
                  <tr>
                    <th className="text-left p-4">Code</th>
                    <th className="text-left p-4">Groupe</th>
                    <th className="text-left p-4">Représentant</th>
                    <th className="text-left p-4">Participants</th>
                    <th className="text-left p-4">Collecté</th>
                    <th className="text-left p-4">Statut / Expire</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((r) => {
                    const live = r.live_status || r.status;
                    const statusStyle = {
                      active: "text-[#206F18] bg-[#E8F6E5]",
                      expired: "text-[#A60F6E] bg-[#FCE7F3]",
                      completed: "text-[#2A0E3C] bg-[#EFE3F5]",
                      cancelled: "text-[#5C4664] bg-[#F2E2EC]",
                    }[live] || "text-[#5C4664] bg-[#F2E2EC]";
                    // Compute "expiring soon" — active + remaining > 0 + < 24h to expiry
                    const expiresMs = r.expires_at ? new Date(r.expires_at).getTime() - Date.now() : null;
                    const expiringSoon =
                      live === "active" &&
                      (r.remaining_amount || 0) > 0 &&
                      expiresMs !== null && expiresMs > 0 && expiresMs < 24 * 3600 * 1000;
                    const rowTint = expiringSoon ? "bg-[#FFF4E1]" : "";
                    // WhatsApp re-engagement message
                    const phoneDigits = (r.creator_phone || "").replace(/[^0-9]/g, "");
                    const waMsg =
                      `Bonjour ${r.creator_name || ""}, votre cagnotte "${r.group_name || ""}" pour ` +
                      `l'événement "${event.title || ""}" expire bientôt. Il reste $${(r.remaining_amount || 0).toFixed(0)} ` +
                      `à collecter sur $${(r.target_amount || 0).toFixed(0)}. Pensez à relancer vos amis 🙏 — Milkado`;
                    const waUrl = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(waMsg)}`;
                    return (
                      <React.Fragment key={r.id}>
                      <tr data-testid={`reservation-row-${r.id}`} className={`border-t border-[#F2E2EC] ${rowTint}`}>
                        <td className="p-4">
                          <span className="font-mono font-bold text-[#D81B8C] tracking-widest">{r.code}</span>
                          {expiringSoon && (
                            <div
                              data-testid={`reservation-expiring-soon-${r.id}`}
                              className="mt-1 inline-flex items-center gap-1 px-1.5 h-4 rounded-full bg-[#FFE9B0] text-[#9A6500] text-[9px] font-bold uppercase tracking-widest"
                            >
                              Expire bientôt
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-[#2A0E3C] font-medium">{r.group_name}</td>
                        <td className="p-4 text-[#5C4664] text-xs">
                          {r.creator_name}<br />{r.creator_phone}
                        </td>
                        <td className="p-4 text-[#5C4664] text-xs">
                          {r.paid_participants_count || 0}/{r.participants_count}
                        </td>
                        <td className="p-4">
                          <div className="font-display font-black text-[#2A0E3C] tabular-nums">
                            ${(r.paid_amount || 0).toFixed(0)}
                            <span className="text-[#5C4664] font-normal">/${(r.target_amount || 0).toFixed(0)}</span>
                          </div>
                          <div className="text-[10px] text-[#5C4664] mt-0.5">
                            Reste ${(r.remaining_amount || 0).toFixed(0)}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase ${statusStyle}`}>
                            {live}
                          </span>
                          <div className="text-[10px] text-[#5C4664] mt-1">
                            {new Date(r.expires_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                            <button
                              onClick={() => toggleReservation(r.id)}
                              data-testid={`expand-reservation-${r.id}`}
                              aria-label="Voir le détail"
                              className="w-8 h-8 rounded-full border border-[#F2E2EC] hover:border-[#D81B8C] flex items-center justify-center text-[#5C4664] hover:text-[#D81B8C] transition-colors"
                            >
                              {expandedReservation === r.id ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {expiringSoon && phoneDigits && (
                              <a
                                href={waUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`reengage-reservation-${r.id}`}
                                className="inline-flex items-center gap-1 rounded-full bg-[#FF8A00] hover:bg-[#E07900] text-white px-3 h-8 text-xs font-semibold transition-colors"
                              >
                                Relancer
                              </a>
                            )}
                            {(live === "expired" || live === "active") && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setExtendPromptFor(r)}
                                data-testid={`extend-reservation-${r.id}`}
                                className="rounded-full border-[#F2E2EC] text-xs"
                              >
                                + jours
                              </Button>
                            )}
                            {live === "active" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditReservation(r)}
                                data-testid={`edit-reservation-${r.id}`}
                                className="rounded-full border-[#F2E2EC] text-xs"
                                title="Modifier la cagnotte"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedReservation === r.id && (
                        <tr
                          data-testid={`reservation-details-${r.id}`}
                          className="border-t border-[#F2E2EC] bg-[#FFF9FB]"
                        >
                          <td colSpan={7} className="p-5">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664] mb-1">
                                  Cadeau
                                </div>
                                <div className="font-display font-black text-[#2A0E3C]">
                                  {r.product_name || "—"}
                                </div>
                                <div className="text-xs text-[#5C4664] mt-0.5">
                                  Prix total : ${(r.target_amount || 0).toFixed(0)} (TCI)
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664] mb-1">
                                  Représentant
                                </div>
                                <div className="text-sm text-[#2A0E3C]">
                                  {r.creator_name}
                                </div>
                                <div className="text-xs text-[#5C4664]">
                                  {r.creator_phone}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664] mb-1">
                                  Avancement
                                </div>
                                <div className="text-sm text-[#2A0E3C]">
                                  {r.paid_participants_count || 0} / {r.participants_count} participants
                                </div>
                                <div className="text-xs text-[#5C4664]">
                                  Collecté ${(r.paid_amount || 0).toFixed(0)} · Reste ${(r.remaining_amount || 0).toFixed(0)}
                                </div>
                              </div>
                            </div>
                            <div className="mt-5">
                              <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664] mb-2">
                                Participants ayant cotisé
                              </div>
                              {(r.participants_paid || []).length === 0 ? (
                                <div className="text-sm text-[#5C4664] italic">
                                  Aucune cotisation reçue pour l&apos;instant.
                                </div>
                              ) : (
                                <table className="w-full text-xs">
                                  <thead className="text-[#5C4664] uppercase tracking-widest text-[10px]">
                                    <tr>
                                      <th className="text-left py-2">Nom</th>
                                      <th className="text-left py-2">Téléphone</th>
                                      <th className="text-left py-2">Mode</th>
                                      <th className="text-left py-2">Date</th>
                                      <th className="text-right py-2">Montant</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.participants_paid.map((p, i) => (
                                      <tr
                                        key={`${p.name || "anon"}-${p.paid_at || i}`}
                                        className="border-t border-[#F2E2EC]"
                                        data-testid={`reservation-participant-${r.id}-${i}`}
                                      >
                                        <td className="py-2 text-[#2A0E3C] font-medium">{p.name}</td>
                                        <td className="py-2 text-[#5C4664]">{p.phone || "—"}</td>
                                        <td className="py-2 text-[#5C4664] uppercase">{p.payment_method}</td>
                                        <td className="py-2 text-[#5C4664]">
                                          {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="py-2 text-right font-bold text-[#2A0E3C]">
                                          ${(p.amount || 0).toFixed(0)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Cadeaux offerts — 1 line per gift (cagnottes counted once, not per participant) */}
        <div className="mt-14">
          <h2 className="font-display text-2xl font-black text-[#2A0E3C] mb-5">
            {t("admin.offeredGifts")} ({offeredGifts.length})
          </h2>
          {offeredGifts.length === 0 ? (
            <div className="py-10 text-center text-[#5C4664] bg-white rounded-2xl border border-[#F2E2EC]">
              {t("admin.noContribs")}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-[#F2E2EC] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FFF9FB] text-[#5C4664] uppercase text-xs tracking-widest">
                  <tr>
                    <th className="text-left p-4">Date</th>
                    <th className="text-left p-4">Invité / Groupe</th>
                    <th className="text-left p-4">Téléphone</th>
                    <th className="text-left p-4">Cadeau</th>
                    <th className="text-left p-4">Mode</th>
                    <th className="text-right p-4">Prix</th>
                    <th className="text-left p-4">Statut</th>
                    <th className="text-right p-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {offeredGifts.map((g) => {
                    const isGroup = g.type === "group";
                    const status = g.gift_order_status;
                    const hasOrder = !!g.gift_order_id;
                    const terminal = ["livre", "annule"].includes(status);
                    // Swap rules:
                    let canSwap = hasOrder && !terminal && status !== "remis" && !isCancelled;
                    let swapDisabledReason = "";
                    if (isCancelled) swapDisabledReason = "Événement annulé — aucune modification possible.";
                    else if (!hasOrder) swapDisabledReason = "Aucun cadeau livrable n'est encore associé (cagnotte en cours).";
                    else if (terminal) swapDisabledReason = `Cadeau ${status === "livre" ? "livré" : "annulé"} — action indisponible.`;
                    else if (status === "remis") swapDisabledReason = "Cadeau déjà remis à l'hôte — changement impossible.";
                    else if (status === "achete" && event?.frozen_at && new Date(event.frozen_at) <= new Date()) {
                      canSwap = false;
                      swapDisabledReason = "Cadeau acheté et événement à moins de 48h — changement impossible. Annulez si nécessaire.";
                    }
                    const canCancel = hasOrder && !terminal && status !== "remis" && !isCancelled;
                    let cancelDisabledReason = "";
                    if (isCancelled) cancelDisabledReason = "Événement annulé — aucune modification possible.";
                    else if (!hasOrder) cancelDisabledReason = "Aucun cadeau livrable n'est encore associé (cagnotte en cours).";
                    else if (terminal) cancelDisabledReason = `Cadeau ${status === "livre" ? "livré" : "annulé"} — action indisponible.`;
                    else if (status === "remis") cancelDisabledReason = "Cadeau déjà remis à l'hôte — annulation impossible.";
                    const refundEstimate = typeof g.estimated_refund === "number" ? g.estimated_refund : null;
                    const cancelTooltip = canCancel
                      ? (refundEstimate !== null
                        ? `Annuler le cadeau — remboursable ≈ $${refundEstimate.toFixed(0)} (fiche Finance, prix fournisseur uniquement).`
                        : "Annuler le cadeau (fiche de remboursement)")
                      : cancelDisabledReason;
                    const showActions = hasOrder;
                    const expanded = expandedOfferedGift === g.id;

                    // Build the contribution-like payload for shared modals.
                    const modalPayload = {
                      id: g.contribution_id || g.reservation_id,
                      gift_order_id: g.gift_order_id,
                      product_id: g.product_id,
                      product_name: g.product_name,
                      estimated_refund: g.estimated_refund,
                      contributor_name: isGroup ? g.group_name : g.guest_name,
                      contributor_phone: isGroup ? g.creator_phone : g.guest_phone,
                    };

                    return (
                      <React.Fragment key={g.id}>
                        <tr data-testid={`offered-row-${g.id}`} className="border-t border-[#F2E2EC]">
                          <td className="p-4 text-[#5C4664] whitespace-nowrap">
                            {new Date(g.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-4 text-[#2A0E3C] font-medium">
                            <div className="flex items-center gap-2">
                              {isGroup ? (
                                <button
                                  onClick={() => setExpandedOfferedGift(expanded ? null : g.id)}
                                  data-testid={`offered-expand-${g.id}`}
                                  className="w-6 h-6 rounded-full border border-[#F2E2EC] hover:border-[#D81B8C] text-[#5C4664] hover:text-[#D81B8C] flex items-center justify-center shrink-0"
                                  title={expanded ? "Réduire" : "Voir les participants"}
                                >
                                  {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                </button>
                              ) : (
                                <span className="w-6 h-6 rounded-full bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center text-[10px] font-bold shrink-0">1</span>
                              )}
                              <div className="min-w-0">
                                {isGroup ? (
                                  <>
                                    <div className="truncate">👥 {g.group_name}</div>
                                    <div className="text-[10px] text-[#5C4664] font-normal">
                                      {g.paid_participants_count}/{g.participants_count} participants
                                    </div>
                                  </>
                                ) : (
                                  <div className="truncate">{g.guest_name || t("common.anonymous")}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-[#5C4664] text-xs">
                            {isGroup ? (g.creator_phone || "—") : (g.guest_phone || "—")}
                          </td>
                          <td className="p-4 text-[#2A0E3C]">
                            {g.product_name || "—"}
                          </td>
                          <td className="p-4 text-[#5C4664] uppercase text-xs tracking-wider">
                            {isGroup ? "cagnotte" : (g.payment_method || "—")}
                          </td>
                          <td className="p-4 text-right font-bold text-[#2A0E3C] whitespace-nowrap">
                            {isGroup ? (
                              <div>
                                <div>${(g.collected_amount || 0).toFixed(0)}</div>
                                <div className="text-[10px] text-[#5C4664] font-normal">/ ${(g.target_amount || 0).toFixed(0)}</div>
                              </div>
                            ) : (
                              `$${(g.amount || 0).toFixed(0)}`
                            )}
                          </td>
                          <td className="p-4">
                            {isGroup && !hasOrder ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#FCE7F3] text-[#D81B8C] whitespace-nowrap">
                                {g.live_status === "completed" ? "Complète" : "En cours"}
                              </span>
                            ) : (
                              <GiftStatusPill status={g.gift_order_status} />
                            )}
                          </td>
                          <td className="p-4">
                            {showActions ? (
                              <div className="flex justify-end gap-1.5">
                                <button
                                  onClick={() => canSwap && setSwapContribution(modalPayload)}
                                  disabled={!canSwap}
                                  data-testid={`offered-swap-${g.id}`}
                                  title={canSwap ? "Changer le cadeau" : swapDisabledReason}
                                  className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
                                    canSwap
                                      ? "border-[#F2E2EC] hover:border-[#D81B8C] text-[#5C4664] hover:text-[#D81B8C] cursor-pointer"
                                      : "border-[#F2E2EC] text-[#C9B5C2] bg-[#FBF6F9] cursor-not-allowed"
                                  }`}
                                >
                                  <RefreshCw className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => canCancel && setCancelContribution(modalPayload)}
                                  disabled={!canCancel}
                                  data-testid={`offered-cancel-${g.id}`}
                                  title={cancelTooltip}
                                  className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
                                    canCancel
                                      ? "border-[#F2E2EC] hover:border-red-500 text-[#5C4664] hover:text-red-600 cursor-pointer"
                                      : "border-[#F2E2EC] text-[#C9B5C2] bg-[#FBF6F9] cursor-not-allowed"
                                  }`}
                                >
                                  <Ban className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-[#5C4664] flex justify-end pr-2">—</span>
                            )}
                          </td>
                        </tr>

                        {/* Participants row (only for group entries when expanded) */}
                        {isGroup && expanded && (
                          <tr data-testid={`offered-participants-${g.id}`} className="border-t border-[#F2E2EC] bg-[#FFF9FB]">
                            <td colSpan={8} className="p-4">
                              <div className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664] mb-2">
                                Participants ayant cotisé
                              </div>
                              {(g.participants || []).length === 0 ? (
                                <div className="text-xs text-[#5C4664]">Aucun paiement encore reçu.</div>
                              ) : (
                                <table className="w-full text-xs">
                                  <thead className="text-[#5C4664]">
                                    <tr>
                                      <th className="text-left py-1.5 pr-4">Nom</th>
                                      <th className="text-left py-1.5 pr-4">Téléphone</th>
                                      <th className="text-left py-1.5 pr-4">Mode</th>
                                      <th className="text-left py-1.5 pr-4">Date</th>
                                      <th className="text-right py-1.5">Montant</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {g.participants.map((p, i) => (
                                      <tr key={`${p.paid_at || i}-${p.name || "anon"}`} className="border-t border-[#F2E2EC]">
                                        <td className="py-1.5 pr-4 text-[#2A0E3C]">{p.name || t("common.anonymous")}</td>
                                        <td className="py-1.5 pr-4 text-[#5C4664]">{p.phone || "—"}</td>
                                        <td className="py-1.5 pr-4 text-[#5C4664] uppercase">{p.payment_method || "—"}</td>
                                        <td className="py-1.5 pr-4 text-[#5C4664]">
                                          {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="py-1.5 text-right font-bold text-[#2A0E3C]">
                                          ${(p.amount || 0).toFixed(0)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ProductPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        eventId={id}
        selectedIds={event.wishlist_product_ids || []}
        onSaved={() => { setPickerOpen(false); load(); }}
      />
      <EditReservationModal
        reservation={editReservation}
        onClose={() => setEditReservation(null)}
        onSaved={() => load()}
      />
      <CancelGiftModal
        open={!!cancelContribution}
        contribution={cancelContribution}
        onClose={() => setCancelContribution(null)}
        onCancelled={() => load()}
      />
      <ProductSwapPicker
        open={!!swapContribution}
        onClose={() => setSwapContribution(null)}
        onSelect={(newProduct) => {
          // Defer the reason prompt to a branded modal (opens after picker).
          setSwapReasonFor({ newProduct, contribution: swapContribution });
          setSwapContribution(null);
        }}
        currentProductId={swapContribution?.product_id}
        currentCategory={
          (() => {
            const wlItem = (event?.wishlist || []).find((p) => p.id === swapContribution?.product_id);
            return wlItem?.category;
          })()
        }
        currentPrice={swapContribution?.amount || 0}
      />

      {/* Branded modals replacing window.confirm / window.prompt */}
      <ConfirmModal
        open={cancelEventOpen}
        onClose={() => setCancelEventOpen(false)}
        onConfirm={async () => {
          setCancelEventOpen(false);
          await cancelEvent();
        }}
        title={t("admin.cancelEvent")}
        description={t("admin.cancelEventConfirm")}
        confirmLabel={t("admin.cancelEvent")}
        cancelLabel={t("common.cancel") || "Annuler"}
        destructive
        testId="cancel-event-confirm"
      />

      <PromptModal
        open={!!extendPromptFor}
        onClose={() => setExtendPromptFor(null)}
        onSubmit={submitExtendReservation}
        title={`Étendre la cagnotte ${extendPromptFor?.code || ""}`}
        description="Nombre de jours d'extension (max 60). La nouvelle date d'expiration ne pourra pas dépasser H-48 de l'événement."
        placeholder="7"
        defaultValue="7"
        kind="number"
        min={1}
        max={60}
        submitLabel="Étendre"
        testId="extend-reservation-modal"
      />

      <PromptModal
        open={!!swapReasonFor}
        onClose={() => setSwapReasonFor(null)}
        onSubmit={async (reason) => {
          const { newProduct, contribution } = swapReasonFor || {};
          setSwapReasonFor(null);
          if (!newProduct || !contribution) return;
          try {
            await api.post(
              `/admin/gift-orders/${contribution.gift_order_id}/swap-product`,
              { new_product_id: newProduct.id, reason },
            );
            toast.success("Cadeau changé avec succès.");
            load();
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Échec du changement de cadeau.");
          }
        }}
        title={swapReasonFor ? `Changer : ${swapReasonFor.contribution?.product_name} → ${swapReasonFor.newProduct?.name}` : ""}
        description="Indiquez la raison du changement de cadeau (audit interne)."
        placeholder="Ex: Rupture de stock chez le fournisseur"
        kind="textarea"
        submitLabel="Valider le changement"
        testId="swap-reason-modal"
      />
    </div>
  );
}

function ProductPickerDialog({ open, onClose, eventId, selectedIds, onSaved }) {
  const { t, lang } = useLanguage();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(new Set(selectedIds));
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    if (open) {
      api.get("/admin/products").then((r) => setProducts(r.data));
      setSelected(new Set(selectedIds));
      setActiveCat("all");
      setSearch("");
    }
  }, [open]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    await api.patch(`/admin/events/${eventId}`, {
      wishlist_product_ids: Array.from(selected),
    });
    onSaved();
  };

  const filtered = products.filter((p) => {
    if (search && !`${p.name} ${p.description}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeCat === "all") return true;
    if (activeCat === "special") return p.is_special;
    return p.category === activeCat;
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-[#2A0E3C]">
            {t("admin.pickerTitle")}
          </DialogTitle>
          <DialogDescription>
            {selected.size} produit(s) sélectionné(s)
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C4664]" />
          <Input
            data-testid="picker-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans le catalogue…"
            className="pl-9 h-11 rounded-xl border-[#F2E2EC]"
          />
        </div>

        {/* Category chips */}
        <div className="-mx-1 px-1 overflow-x-auto pb-1">
          <div className="flex gap-2 min-w-max">
            {[
              { slug: "all", label: lang === "fr" ? "Toutes" : "All" },
              ...CATEGORIES.map((c) => ({ slug: c.slug, label: categoryLabel(c.slug, lang), Icon: c.icon, color: c.color })),
              { slug: "special", label: categoryLabel("special", lang), color: "#6B2C8F" },
            ].map((tab) => {
              const active = activeCat === tab.slug;
              return (
                <button
                  key={tab.slug}
                  onClick={() => setActiveCat(tab.slug)}
                  data-testid={`picker-cat-${tab.slug}`}
                  className={`flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                    active
                      ? "bg-[#2A0E3C] text-white"
                      : "bg-white text-[#2A0E3C] border border-[#F2E2EC] hover:border-[#D81B8C]/40"
                  }`}
                >
                  {tab.Icon ? <tab.Icon className="w-3.5 h-3.5" style={{ color: active ? "#fff" : tab.color }} /> : null}
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {filtered.map((p) => {
              const isChecked = selected.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggle(p.id)}
                  data-testid={`picker-item-${p.id}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors ${
                    isChecked ? "border-[#D81B8C] bg-[#FCE7F3]" : "border-[#F2E2EC] hover:border-[#2A0E3C]/30"
                  }`}
                >
                  {p.image_url && (
                    <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-[#5C4664]">
                      {categoryLabel(p.category, lang)}
                    </div>
                    <div className="font-display text-xs font-bold text-[#2A0E3C] leading-snug line-clamp-2">
                      {p.name}
                    </div>
                    <div className="text-xs text-[#D81B8C] font-bold">${p.price.toFixed(0)}</div>
                  </div>
                  {isChecked && (
                    <Check className="w-5 h-5 text-[#D81B8C] shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="picker-cancel"
            className="rounded-full flex-1 border-[#F2E2EC]"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={save}
            data-testid="picker-save"
            className="rounded-full flex-1 bg-[#D81B8C] hover:bg-[#A60F6E]"
          >
            {t("admin.pickerDone")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
